import type {
  ChannelKeyboardButton,
  ChannelMessage,
  CommunicationChannel,
} from '@/api/channelInbox'
import { CHANNELS } from '@/api/channelInbox'
import { buildApiUrl } from '@/api/http'
import type { PortalChatConversation } from '@/api/portalMessaging'

export function portalConversationMatchesSearch(conversation: PortalChatConversation, search: string): boolean {
  const needle = search.trim().toLocaleLowerCase('ru-RU')
  return !needle || conversation.title.toLocaleLowerCase('ru-RU').includes(needle)
}

export interface ChannelMeta {
  title: string
  icon: string
  color: string
}

/**
 * Telegram messages carry HTML markup (b, i, a, code...). The inbox must render
 * that as HTML rather than literal "<b>", but never trust the channel blindly.
 * This keeps a strict allow-list of tags/attributes; anything else is dropped,
 * so a stray <script> or onerror handler from a message body cannot run.
 */
const ALLOWED_TAGS = new Set([
  'B', 'STRONG', 'I', 'EM', 'U', 'INS', 'S', 'STRIKE', 'DEL',
  'A', 'CODE', 'PRE', 'BLOCKQUOTE', 'BR', 'SPAN', 'TG-EMOJI',
])
const ALLOWED_ATTRS: Record<string, Set<string>> = {
  A: new Set(['href', 'title']),
  'TG-EMOJI': new Set(['emoji-id']),
}

export function sanitizeTelegramHtml(input: string): string {
  if (!input) return ''
  const doc = new DOMParser().parseFromString(input, 'text/html')
  const walk = (node: Node): Node[] => {
    const removed: Node[] = []
    node.childNodes.forEach(child => {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const el = child as Element
        const tag = el.tagName.toUpperCase()
        if (!ALLOWED_TAGS.has(tag)) {
          // Unwrap disallowed elements: keep their text children, drop the element.
          walk(el)
          removed.push(el)
          return
        }
        // Strip every attribute not on the allow-list for this tag.
        Array.from(el.attributes).forEach(attr => {
          const allowed = ALLOWED_ATTRS[tag]
          if (!allowed || !allowed.has(attr.name.toLowerCase())) {
            el.removeAttribute(attr.name)
          } else if (attr.name.toLowerCase() === 'href') {
            // Only http(s) links may survive; javascript: and data: must not.
            if (!/^(https?:|mailto:)/i.test(attr.value.trim())) el.removeAttribute(attr.name)
          }
        })
        walk(el)
      }
    })
    // Unwrap in a second pass so iteration above is not invalidated.
    removed.forEach(el => {
      const parent = el.parentNode
      if (!parent) return
      while (el.firstChild) parent.insertBefore(el.firstChild, el)
      parent.removeChild(el)
    })
    return removed
  }
  walk(doc.body)
  return doc.body.innerHTML
}

/** Plain-text version of a message body, for list previews where markup is noise. */
export function stripHtml(input: string | null | undefined): string {
  if (!input) return ''
  const doc = new DOMParser().parseFromString(input, 'text/html')
  return (doc.body.textContent || '').replace(/\s+/g, ' ').trim()
}

export const CHANNEL_META: Record<CommunicationChannel, ChannelMeta> = {
  telegram: { title: 'Telegram', icon: '$avoChannelTelegram', color: 'info' },
  vk: { title: 'ВКонтакте', icon: '$avoChannelVk', color: 'primary' },
  avito: { title: 'Avito', icon: '$avoChannelAvito', color: 'warning' },
  max: { title: 'MAX', icon: '$avoChannelMax', color: 'info' },
}

export function isCommunicationChannel(value: unknown): value is CommunicationChannel {
  return typeof value === 'string' && CHANNELS.includes(value as CommunicationChannel)
}

export function safeChannelUrl(value: string | null | undefined): string | null {
  const source = value?.trim()
  if (!source) return null

  try {
    const base = typeof window === 'undefined' ? 'https://avoschool.invalid' : window.location.origin
    const url = new URL(source, base)
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null
    return source.startsWith('/') && url.origin === new URL(base).origin
      ? `${url.pathname}${url.search}${url.hash}` : url.toString()
  } catch {
    return null
  }
}

export interface ChannelMessagePresentation {
  kind: ChannelMessage['type']
  text: string
  url: string | null
  previewUrl: string | null
  title: string | null
  mimeType: string | null
}

export function channelMessagePresentation(message: ChannelMessage): ChannelMessagePresentation {
  const content = message.content
  const fallbackText = message.text?.trim() || ''

  switch (message.type) {
    case 'image': {
      // Telegram file IDs and MAX media are resolved through our private API:
      // provider URLs may expire or require credentials and are outside the CSP.
      const fileId = content?.image?.fileId?.trim() || content?.image?.file_id?.trim()
      const privateUrl = !message.deletedAt && (message.channel === 'max' || (message.channel === 'telegram' && fileId))
        ? buildApiUrl(`/channel-inbox/conversations/${encodeURIComponent(message.conversationId)}/messages/${encodeURIComponent(message.id)}/image`)
        : null
      const url = message.channel === 'max' ? privateUrl : safeChannelUrl(content?.image?.url) ?? privateUrl
      return {
        kind: 'image',
        text: fallbackText,
        url,
        previewUrl: message.channel === 'max' ? url : safeChannelUrl(content?.image?.previewUrl) ?? url,
        title: content?.image?.alt?.trim() || null,
        mimeType: null,
      }
    }
    case 'link':
      return {
        kind: 'link',
        text: fallbackText,
        url: safeChannelUrl(content?.link?.url),
        previewUrl: null,
        title: content?.link?.title?.trim() || null,
        mimeType: null,
      }
    case 'audio':
    case 'voice':
      return {
        kind: 'voice',
        text: fallbackText,
        // Provider links can expire, require bot credentials, or be blocked by
        // the site's CSP. Resolve the exact message through the authenticated API.
        url: buildApiUrl(`/channel-inbox/conversations/${encodeURIComponent(message.conversationId)}/messages/${encodeURIComponent(message.id)}/voice`),
        previewUrl: null,
        title: content?.voice?.durationSeconds
          ? `Голосовое · ${content.voice.durationSeconds} сек.`
          : 'Голосовое сообщение',
        mimeType: content?.voice?.mimeType || null,
      }
    case 'document':
      return {
        kind: 'document',
        text: fallbackText,
        url: message.deletedAt ? null : message.channel === 'max'
          ? buildApiUrl(`/channel-inbox/conversations/${encodeURIComponent(message.conversationId)}/messages/${encodeURIComponent(message.id)}/file`)
          : safeChannelUrl(content?.document?.url),
        previewUrl: null,
        title: content?.document?.name?.trim() || 'Документ',
        mimeType: content?.document?.mimeType?.trim() || null,
      }
    case 'item':
      return {
        kind: 'item',
        text: fallbackText,
        url: safeChannelUrl(content?.item?.url),
        previewUrl: null,
        title: content?.item?.title?.trim() || 'Объявление',
        mimeType: null,
      }
    case 'service':
      return {
        kind: 'service',
        text: content?.service?.text?.trim() || fallbackText || 'Системное сообщение',
        url: null,
        previewUrl: null,
        title: null,
        mimeType: null,
      }
    case 'text':
      return {
        kind: 'text',
        text: fallbackText,
        url: null,
        previewUrl: null,
        title: null,
        mimeType: null,
      }
    default:
      // The backend allows more message types than the UI enumerates
      // (photo, file, system, ...). Render them as plain text instead
      // of crashing the thread on an undefined presentation.
      return {
        kind: 'text',
        text: fallbackText || `[${message.type}]`,
        url: null,
        previewUrl: null,
        title: null,
        mimeType: null,
      }
  }
}

export function channelDirectionLabel(direction: ChannelMessage['direction']): string {
  return direction === 'outbound' ? 'Вы' : 'Клиент'
}

/** Inline buttons attached to a message, if any. Empty when the message has none. */
export function messageKeyboard(message: ChannelMessage): ChannelKeyboardButton[][] {
  const rows = message.content?.keyboard
  return Array.isArray(rows) && rows.length > 0 ? rows : []
}

/**
 * A button is interactive in Telegram only while the server-side prompt token
 * behind it is live. Once it expires the button is still shown (for context),
 * but we render it as a static label rather than something clickable.
 */
export function isLiveKeyboardButton(button: ChannelKeyboardButton): boolean {
  // The bot mints 24-hex-char opaque tokens; url buttons stay live forever.
  return !!button.url || /^[0-9a-f]{24}$/i.test(button.callbackData ?? '')
}
